/* eslint-disable import/no-cycle */
import { createTypes, completeTypes, withPostSuccess } from 'redux-recompose';
import withPostFailure from 'redux-recompose/lib/injections/withPostFailure';

import ModalActions from '~redux/Modal/actions';
import OrderTakerActions from '~redux/OrderTaker/actions';
import { sendCart } from '~services/Cart/service';
import { MODALS } from '~redux/Modal/constants';
import {
  addItemToCart,
  priceCart,
  removeItemFromCart,
  updateItemInCart,
  deleteItemFromCart,
  checkCartDenormalize
} from '~utils/product';

import { targets } from './constants';

export const actions = createTypes(
  completeTypes(
    [
      'CHECK_CART',
      'CLEAR_CART',
      'CREATE_REMOTE_CART',
      'UPDATE_REMOTE_CART',
      'GET_LAST_REMOTE_CART',
      'GET_CART_BY_ID'
    ],
    [
      'SET_ITEM',
      'SET_CART',
      'SET_FINAL_PRICE',
      'SET_SUBTOTAL_PRICE',
      'SET_ERROR',
      'SET_DISCOUNT',
      'SET_DELIVERY',
      'ADD_ITEM',
      'SUM_ITEM',
      'REMOVE_ITEM',
      'MODIFY_ITEM',
      'SET_CART_LOADING',
      'DELETE_REMOTE_CART',
      'RESTORE_CART',
      'REMOVE_COUPON',
      'ADD_COUPON'
    ]
  ),
  '@@CART'
);

const getCartPayload = ({ item, state, mapper }) => {
  const cartArray = Array.isArray(state.cart)
    ? state.cart
    : state.cart?.asMutable
    ? state.cart.asMutable()
    : [];

  const cartState = { cart: cartArray };

  return mapper(item)(cartState);
};

const addItemDefaultOptions = {
  skipAddToCartEvent: false,
  skipRemoveFromCartEvent: false,
  skipCheckScheduledDateAvailability: false
};

export const actionCreators = {
  setCartLoading: (loading) => (dispatch) => {
    dispatch({
      type: actions.SET_CART_LOADING,
      target: `${targets.CART}Loading`,
      payload: Boolean(loading)
    });
  },
  setCart: (cart) => (dispatch) => {
    dispatch({
      type: actions.SET_CART,
      target: targets.CART,
      payload: cart
    });
  },
  addItem:
    ({ item, closeUrl, checkCart }, { skipCheckScheduledDateAvailability } = addItemDefaultOptions) =>
    (dispatch, getState) => {
      const state = getState();
      const payload = getCartPayload({ item, state: state.cart, mapper: addItemToCart });

      if (closeUrl !== undefined) {
        dispatch(ModalActions.closeModal(MODALS.PRODUCT_DETAIL));
      }
      dispatch(actionCreators.setCartLoading(true));
      dispatch({
        type: actions.ADD_ITEM,
        target: targets.CART,
        payload
      });
      if (checkCart) {
        dispatch(actionCreators.checkCart());
      }
      console.log('skipCheckScheduledDateAvailability', skipCheckScheduledDateAvailability);
      dispatch(actionCreators.setSubtotalPrice(priceCart(payload.cart)));
    },
  removeItem:
    ({ item, checkCart } = {}) =>
    (dispatch, getState) => {
      const state = getState();

      const payload = getCartPayload({ item, state: state.cart, mapper: removeItemFromCart });

      dispatch(actionCreators.setCartLoading(true));
      dispatch({
        type: actions.REMOVE_ITEM,
        target: targets.CART,
        payload
      });

      const newCart = payload.cart || [];
      const isEmpty = Array.isArray(newCart) ? newCart.length === 0 : true;

      // If the cart is empty, reset local totals and clear coupon synchronously
      if (isEmpty) {
        dispatch({ type: actions.SET_FINAL_PRICE, target: targets.FINAL_PRICE, payload: 0 });
        dispatch({ type: actions.SET_SUBTOTAL_PRICE, target: targets.SUBTOTAL_PRICE, payload: 0 });
        dispatch({ type: actions.SET_DISCOUNT, target: targets.DISCOUNT, payload: 0 });
        dispatch({ type: actions.SET_DELIVERY, target: targets.DELIVERY, payload: 0 });
        // Clear coupon in both reducers without triggering a new checkCart
        dispatch({ type: actions.REMOVE_COUPON, target: targets.CART });
        dispatch(OrderTakerActions.setCoupon(''));
        dispatch(actionCreators.setCartLoading(false));
      } else if (checkCart) {
        dispatch(actionCreators.checkCart());
      } else {
        dispatch(actionCreators.setCartLoading(false));
      }

      dispatch(actionCreators.setSubtotalPrice(priceCart(newCart)));
    },
  deleteItem:
    ({ item, checkCart } = {}) =>
    (dispatch, getState) => {
      const state = getState();

      const payload = getCartPayload({ item, state: state.cart, mapper: deleteItemFromCart });

      dispatch(actionCreators.setCartLoading(true));
      dispatch({
        type: actions.REMOVE_ITEM,
        target: targets.CART,
        payload
      });

      const newCart = payload.cart || [];
      const isEmpty = Array.isArray(newCart) ? newCart.length === 0 : true;

      if (isEmpty) {
        dispatch({ type: actions.SET_FINAL_PRICE, target: targets.FINAL_PRICE, payload: 0 });
        dispatch({ type: actions.SET_SUBTOTAL_PRICE, target: targets.SUBTOTAL_PRICE, payload: 0 });
        dispatch({ type: actions.SET_DISCOUNT, target: targets.DISCOUNT, payload: 0 });
        dispatch({ type: actions.SET_DELIVERY, target: targets.DELIVERY, payload: 0 });
        dispatch({ type: actions.REMOVE_COUPON, target: targets.CART });
        dispatch(OrderTakerActions.setCoupon(''));
        dispatch(actionCreators.setCartLoading(false));
      } else if (checkCart) {
        dispatch(actionCreators.checkCart());
      } else {
        dispatch(actionCreators.setCartLoading(false));
      }

      dispatch(actionCreators.setSubtotalPrice(priceCart(newCart)));
    },
  modifyItem:
    ({ oldItem, newItem, checkCart } = {}) =>
    (dispatch, getState) => {
      const state = getState();

      const cartArray = Array.isArray(state.cart.cart)
        ? state.cart.cart
        : state.cart.cart?.asMutable
        ? state.cart.cart.asMutable()
        : [];

      const cartState = { cart: cartArray };
      const payload = updateItemInCart(oldItem, newItem)(cartState);

      dispatch(actionCreators.setCartLoading(true));
      dispatch({
        type: actions.MODIFY_ITEM,
        target: targets.CART,
        payload
      });
      if (checkCart) {
        dispatch(actionCreators.checkCart());
      }
      dispatch(actionCreators.setSubtotalPrice(priceCart(payload.cart)));
    },
  setSubtotalPrice: (subtotal) => (dispatch) => {
    dispatch({
      type: actions.SET_SUBTOTAL_PRICE,
      target: targets.SUBTOTAL_PRICE,
      payload: subtotal
    });
  },
  checkCart: ({ showModalError = false, address = null, discount, dispatchType = null } = {}) => ({
    type: actions.CHECK_CART,
    target: targets.CART,
    payload: checkCartDenormalize(address, discount, dispatchType),
    service: sendCart,
    injections: [
      withPostSuccess((_, response) => {
        console.log('response.data', response.data);
      }),
      withPostFailure((dispatch) => {
        console.log('showModalError', showModalError);
        if (showModalError) {
          dispatch(ModalActions.openModal(MODALS.ERROR_CHECK_CART));
        }
      })
    ]
  }),
  removeCoupon: () => (dispatch) => {
    dispatch({
      type: actions.REMOVE_COUPON,
      target: targets.CART
    });
    dispatch(actionCreators.checkCart());
  },
  clearCart: () => ({
    type: actions.CLEAR_CART
  })
};

export default actionCreators;
